'use client';

/**
 * Data + filter state hook for the teacher assignments list page.
 *
 * Module: app/assignments/use-assignments-list
 * Layer:  hook
 *
 * Purpose:
 *     Loads the teacher's assignments and derives the client-side filtered
 *     view (classroom, status, search) so the page component stays small and
 *     every function clears the CC<=5 / <=60-line gates. The full list is
 *     fetched once; classroom/status/search filtering is applied in memory,
 *     which keeps the UI responsive without a round-trip per keystroke.
 *
 * AI context:
 *     - Entry points called by: src/app/assignments/page.tsx.
 *     - Depends on: lib/assignments (fetch + types).
 *     - Side effects: network load on mount, aborted on unmount.
 *     - Invariants: load failures resolve to an empty list (never thrown to
 *       React); the in-flight load is aborted in cleanup.
 *     - Replacement cost: LOW.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type Assignment,
  type AssignmentStatus,
  fetchAssignments,
} from '@/lib/assignments';

/** A status filter value: a concrete status, or `all` for no filter. */
export type StatusFilter = AssignmentStatus | 'all';

/** A classroom filter value: a concrete id, or `all` for no filter. */
export type ClassroomFilter = number | 'all';

/** Everything the list page needs to render and react to user input. */
export interface AssignmentsListController {
  readonly assignments: readonly Assignment[];
  readonly classroomIds: readonly number[];
  readonly loading: boolean;
  readonly search: string;
  readonly statusFilter: StatusFilter;
  readonly classroomFilter: ClassroomFilter;
  readonly hasFilters: boolean;
  readonly setSearch: (next: string) => void;
  readonly setStatusFilter: (next: StatusFilter) => void;
  readonly setClassroomFilter: (next: ClassroomFilter) => void;
  readonly clearFilters: () => void;
}

/** Loaded assignments plus the in-flight flag. */
interface LoadState {
  readonly assignments: readonly Assignment[];
  readonly loading: boolean;
}

/** Load every assignment once, aborting on unmount. */
function useAssignmentsLoad(): LoadState {
  const [state, setState] = useState<LoadState>({ assignments: [], loading: true });
  useEffect(() => {
    const controller = new AbortController();
    const run = async (): Promise<void> => {
      const rows = await fetchAssignments(undefined, controller.signal);
      if (!controller.signal.aborted) {
        setState({ assignments: rows, loading: false });
      }
    };
    void run();
    return (): void => controller.abort();
  }, []);
  return state;
}

/** The active filter selections applied to the assignment list. */
interface ActiveFilters {
  readonly classroom: ClassroomFilter;
  readonly status: StatusFilter;
  readonly query: string;
}

/** True when an assignment is in the selected classroom (or none selected). */
function classroomOk(assignment: Assignment, classroom: ClassroomFilter): boolean {
  return classroom === 'all' || assignment.classroom_id === classroom;
}

/** True when an assignment has the selected status (or none selected). */
function statusOk(assignment: Assignment, status: StatusFilter): boolean {
  return status === 'all' || assignment.status === status;
}

/** True when an assignment's title contains the search query (or none). */
function searchOk(assignment: Assignment, query: string): boolean {
  return query === '' || assignment.title.toLowerCase().includes(query);
}

/** True when an assignment matches the active classroom + status + search. */
function matchesFilters(assignment: Assignment, filters: ActiveFilters): boolean {
  return (
    classroomOk(assignment, filters.classroom) &&
    statusOk(assignment, filters.status) &&
    searchOk(assignment, filters.query)
  );
}

/** Derive the sorted set of distinct classroom ids present in the data. */
function distinctClassroomIds(assignments: readonly Assignment[]): readonly number[] {
  const ids = new Set<number>(assignments.map((a) => a.classroom_id));
  return [...ids].toSorted((a, b) => a - b);
}

/**
 * Teacher assignments controller: load + in-memory classroom/status/search.
 *
 * @returns An {@link AssignmentsListController} for the list page.
 */
export function useAssignmentsList(): AssignmentsListController {
  const { assignments, loading } = useAssignmentsLoad();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [classroomFilter, setClassroomFilter] = useState<ClassroomFilter>('all');

  const query = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    const filters: ActiveFilters = { classroom: classroomFilter, status: statusFilter, query };
    return assignments.filter((a) => matchesFilters(a, filters));
  }, [assignments, classroomFilter, statusFilter, query]);
  const classroomIds = useMemo(() => distinctClassroomIds(assignments), [assignments]);

  const clearFilters = useCallback((): void => {
    setSearch('');
    setStatusFilter('all');
    setClassroomFilter('all');
  }, []);

  return {
    assignments: filtered,
    classroomIds,
    loading,
    search,
    statusFilter,
    classroomFilter,
    hasFilters: query !== '' || statusFilter !== 'all' || classroomFilter !== 'all',
    setSearch,
    setStatusFilter,
    setClassroomFilter,
    clearFilters,
  };
}
